import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Palette, Terminal, Zap, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const About: React.FC = () => {
    const { t } = useTranslation();
    const reduce = useReducedMotion();

    const stats = [
        { icon: <Terminal className="w-4 h-4" />, label: t('about.stats.stack.label'), value: t('about.stats.stack.value') },
        { icon: <Palette className="w-4 h-4" />, label: t('about.stats.arch.label'), value: t('about.stats.arch.value') },
        { icon: <Zap className="w-4 h-4" />, label: t('about.stats.perf.label'), value: t('about.stats.perf.value') },
        { icon: <Globe className="w-4 h-4" />, label: t('about.stats.deploy.label'), value: t('about.stats.deploy.value') },
    ];

    return (
        <section
            id="about"
            className="scroll-mt-24 border-t border-black/[0.05] bg-white py-16 transition-colors duration-500 md:py-20 dark:border-white/[0.07] dark:bg-[#0f0f13]"
        >
            <div className="max-w-4xl mx-auto px-6 text-center">
                <motion.div
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-10 md:mb-12"
                >
                    <h2 className="text-2xl md:text-4xl font-bold tracking-[-0.02em] text-gray-900 dark:text-white">
                        {t('about.title')}
                    </h2>
                    <p
                        className="mt-4 text-[15px] md:text-base text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-[1.9] [text-wrap:pretty]"
                        dangerouslySetInnerHTML={{ __html: t('about.description') }}
                    />
                </motion.div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {stats.map((stat, i) => (
                        <motion.div
                            key={i}
                            initial={reduce ? false : { opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.08 }}
                            className="p-5 md:p-6 bg-[#f5f5f7] dark:bg-[#18181b] rounded-2xl border border-black/[0.04] dark:border-gray-800 hover:border-blue-200 dark:hover:border-blue-800 transition-colors"
                        >
                            <div className="flex flex-col items-center gap-3">
                                <div className="p-2.5 bg-white dark:bg-gray-800 rounded-full text-gray-900 dark:text-white shadow-sm">
                                    {stat.icon}
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-gray-900 dark:text-white">{stat.value}</div>
                                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{stat.label}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default About;
